import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';

import { Input } from '@eika/input';

import { useValidateBank } from '../../../../fetchHooks/useCrossBorderBankValidation';
import type { BankValidation } from '../../../../generated-types';
import useTranslation from '../../../../hooks/useTranslation';
import { CrossBorderPaymentActionType } from '../form/actions';
import useFormContext from '../form/useFormContext';
import { AmountFieldsError, receiverError, validateBIC } from '../form/validate';

interface Props {
    validationError?: receiverError | AmountFieldsError;
    setValidationError?: (errors: receiverError) => void;
    className?: string;
    enableValidation?: boolean;
}

const BicInput: React.FC<Props> = ({ validationError, setValidationError, className, enableValidation = false }) => {
    const translated = useTranslation();
    const { state, handleChange } = useFormContext();
    const [bic, setBic] = useState(state.receiver?.bic?.value || '');
    const [bicError, setBicError] = useState<string | undefined>();
    const minBicLength = 8;
    const maxBicLength = 11;

    useEffect(() => {
        if (state.receiver?.bic?.value) {
            setBic(state.receiver.bic.value);
        }
    }, [state.receiver?.bic?.value, setBic]);

    const onBankValidation = useCallback(
        (data: BankValidation) => {
            if (!data.valid && enableValidation) {
                setBicError(translated.crossBorderPayment.newReceiver.bicError);
                return;
            }
            handleChange({
                type: CrossBorderPaymentActionType.SET_BANK_VALIDATION,
                payload: data,
            });
        },
        [enableValidation, handleChange, translated.crossBorderPayment.newReceiver.bicError],
    );

    useValidateBank(
        state.receiver?.bic,
        state.receiver?.nationalId,
        state.country?.countryCode,
        !!state.receiver?.bic,
        onBankValidation,
        enableValidation,
    );

    const handleBICChange = (bicString: string) => {
        const validation = validateBIC(translated, bicString);
        setBicError(validation?.bic);

        if (!validation) {
            handleChange({ type: CrossBorderPaymentActionType.SET_RECEIVER_BIC, payload: { value: bicString } });
            handleChange({
                type: CrossBorderPaymentActionType.SET_RECEIVER_ACCOUNT_TYPE,
                payload: 'BBAN',
            });
        } else {
            handleChange({ type: CrossBorderPaymentActionType.SET_RECEIVER_BIC });
            handleChange({ type: CrossBorderPaymentActionType.SET_RECEIVER_ACCOUNT_TYPE });
        }
    };

    return (
        <Input
            className={classNames('cross-border-payment__field-top', className)}
            label={translated.crossBorderPayment.newReceiver.bicLabel}
            value={bic}
            onChange={(value) => {
                if (value.target.value.length === minBicLength || value.target.value.length === maxBicLength) {
                    handleBICChange(value.target.value);
                }
                setBic(value.target.value.toUpperCase());
                setValidationError?.({ bic: undefined });
            }}
            onBlur={() => handleBICChange(bic)}
            error={bicError || validationError?.bic}
        />
    );
};

export default BicInput;
